/**
 * An agent's Daily notes (slice 1m spec §7): a page a day, newest first, each
 * with its lines (a note's time beside it) and who wrote it last. The owner
 * edits a day in place (Write and Preview against 8,000 characters, with a
 * conflict to resolve as a file's) and forgets one behind a confirm.
 * "Show older days" pages back.
 */
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@crew/ui/components/alert-dialog";
import { Button } from "@crew/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@crew/ui/components/card";
import { FieldError } from "@crew/ui/components/field";
import { Spinner } from "@crew/ui/components/spinner";
import { toneText } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";
import { CalendarDays } from "lucide-react";
import { useState } from "react";
import { Streamdown } from "streamdown";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { useNow } from "../../app/now";
import {
  noteConflictOf,
  notesOf,
  refusalOf,
  useForgetNote,
  useMemoryNotes,
  useSaveNote,
  type MemoryNote,
} from "./api";
import { ConflictPanel, WriteAndPreview } from "./file-editor";
import {
  agentPhrase,
  canSaveNote,
  counter,
  dayHeading,
  noteAuthor,
  noteKit,
  noteLines,
  notedBy,
  NOTE_LIMIT,
  overLimit,
  type NoteEditor,
} from "./memory-view";

type Agent = Me["agents"][number];

export function DailyNotes({ agent, me }: { agent: Agent; me: Me }) {
  const notes = useMemoryNotes(agent.id);
  const days = notesOf(notes.data?.pages);
  const who = agentPhrase(agent);
  return (
    <Card data-daily-notes={agent.slug} aria-label="Daily notes" className="gap-0 py-0">
      <CardHeader className="flex flex-col gap-1 px-4 pt-4 pb-3 sm:px-6 sm:pt-5 sm:pb-4">
        <CardTitle className="text-lead font-semibold">Daily notes</CardTitle>
        <p className="text-sm text-muted-foreground">
          What {who} noted as it worked, one page a day. Today's and yesterday's reach every chat;
          it searches older days when it needs them.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col px-0">
        {notes.data ? (
          days.length > 0 ? (
            days.map((note) => <Day key={note.id} note={note} agent={agent} me={me} />)
          ) : (
            <Empty who={who} />
          )
        ) : (
          <div className="border-t border-border px-4 py-6 text-body text-muted-foreground sm:px-6">
            {notes.error ? errorHint(notes.error) : <Spinner className="size-5" />}
          </div>
        )}
        {notes.hasNextPage ? (
          <div className="flex justify-center border-t border-border px-4 pt-3 pb-4">
            <Button
              variant="outline"
              size="sm"
              data-older-days
              disabled={notes.isFetchingNextPage}
              onClick={() => void notes.fetchNextPage()}
            >
              {notes.isFetchingNextPage ? <Spinner data-icon="inline-start" /> : null}
              Show older days
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Empty({ who }: { who: string }) {
  return (
    <div
      data-daily-notes-empty
      className="flex flex-col items-center gap-2 border-t border-border px-6 pt-9 pb-10 text-center"
    >
      <CalendarDays className="size-5 text-hint" strokeWidth={1.5} aria-hidden />
      <p className="text-body font-medium text-foreground">Nothing noted yet</p>
      <p className="max-w-md text-sm text-muted-foreground">
        As {who} works, it notes what happened and what it learned: a booking made, a fact someone
        mentioned, a decision. Each day's notes show here.
      </p>
    </div>
  );
}

/** One day: its heading, Edit and Forget for the owner, its lines (or its editor), who wrote it. */
function Day({ note, agent, me }: { note: MemoryNote; agent: Agent; me: Me }) {
  const now = useNow();
  const [editing, setEditing] = useState<NoteEditor | null>(null);
  const owner = me.user.role === "owner";
  const { title, date } = dayHeading(note.day, me.workspace.timezone, now);
  return (
    <section
      data-note-day={note.day}
      data-version={note.version}
      aria-label={date ? `${title}, ${date}` : title}
      className="flex flex-col gap-2.5 border-t border-border px-4 pt-3.5 pb-4 sm:px-6 sm:pt-4 sm:pb-[18px]"
    >
      <header className="flex items-baseline gap-2.5">
        <h3 className="text-body font-semibold text-foreground">{title}</h3>
        {date ? <span className="text-meta text-hint">{date}</span> : null}
        {owner && !editing ? (
          <div className="ml-auto flex items-center gap-1">
            <Button
              variant="ghost"
              size="xs"
              className="text-muted-foreground"
              data-note-edit
              onClick={() => setEditing(noteKit.editorFor(note))}
            >
              Edit
            </Button>
            <ForgetDay note={note} agent={agent} label={date ? `${title}, ${date}` : title} />
          </div>
        ) : null}
      </header>
      {editing ? (
        <DayEditor
          note={note}
          agent={agent}
          me={me}
          editor={noteKit.receive(editing, note)}
          onChange={(update) => setEditing((e) => (e ? update(noteKit.receive(e, note)) : e))}
          onClose={() => setEditing(null)}
        />
      ) : (
        <Lines content={note.content} />
      )}
      <p className="text-xs text-hint sm:pl-[52px]" data-noted-by>
        {notedBy(note, me, now)}
      </p>
    </section>
  );
}

/** A day's lines with their times in a lane of their own; a day that isn't all notes, as markdown. */
function Lines({ content }: { content: string }) {
  const lines = noteLines(content);
  if (!lines) {
    return (
      <Streamdown
        // A day's headings (an imported OpenClaw note's "# 14 Oct") read as its lines' size.
        className="space-y-2 text-body text-copy [&_h1,&_h2,&_h3,&_h4]:text-body [&_h1,&_h2,&_h3,&_h4]:font-semibold"
        controls={false}
      >
        {content}
      </Streamdown>
    );
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {lines.map((line, i) => (
        <li key={i} className="flex gap-3">
          <span
            aria-hidden={line.time ? undefined : true}
            className="w-10 shrink-0 font-mono text-meta leading-[22px] text-hint tabular-nums"
          >
            {line.time ?? "•"}
          </span>
          <span className="min-w-0 flex-1 text-body leading-[22px] text-copy">{line.text}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The owner's edit of one day, in place: the draft against the day's note,
 * a save from its version, and a conflict (a note the agent added meanwhile)
 * to keep the draft over or take.
 */
function DayEditor({
  note,
  agent,
  me,
  editor,
  onChange,
  onClose,
}: {
  note: MemoryNote;
  agent: Agent;
  me: Me;
  editor: NoteEditor;
  onChange: (update: (e: NoteEditor) => NoteEditor) => void;
  onClose: () => void;
}) {
  const save = useSaveNote(agent.id, note.id);
  const [problem, setProblem] = useState<string | null>(null);
  const count = counter(editor.draft.length, NOTE_LIMIT);
  const tooLong = overLimit(editor.draft.length, NOTE_LIMIT);

  const send = (from: NoteEditor) => {
    const sent = from.draft;
    setProblem(null);
    save.mutate(
      { content: sent, version: from.base.version },
      {
        // The editor is read-only while it saves, so what was sent is the draft: done.
        onSuccess: onClose,
        onError: (error) => {
          const current = noteConflictOf(error);
          if (current) onChange((e) => noteKit.conflicted(e, current));
          else setProblem(refusalOf(error)?.hint ?? errorHint(error));
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-3" data-note-editor>
      {editor.conflict ? (
        <ConflictPanel
          who={noteAuthor(editor.conflict.updated_by, me)}
          content={editor.conflict.content}
          busy={save.isPending}
          onKeepMine={() => {
            const next = noteKit.keepMine(editor);
            onChange(() => next);
            send(next);
          }}
          onUseTheirs={() => onChange(noteKit.takeTheirs)}
        />
      ) : null}
      <WriteAndPreview
        label={`Daily note, ${note.day}`}
        draft={editor.draft}
        readOnly={save.isPending}
        onChange={(text) => onChange((e) => noteKit.edit(e, text))}
        limit={NOTE_LIMIT}
        size="compact"
      />
      {tooLong ? <FieldError>{tooLong}</FieldError> : null}
      {problem ? <FieldError data-refusal>{problem}</FieldError> : null}
      <div className="flex flex-wrap items-center gap-2">
        <span
          data-counter
          className={cn("mr-auto font-mono text-xs tabular-nums", toneText[count.tone])}
        >
          {count.text}
        </span>
        <Button variant="ghost" size="sm" disabled={save.isPending} onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          data-note-save
          disabled={!canSaveNote(editor) || save.isPending}
          onClick={() => send(editor)}
        >
          {save.isPending ? <Spinner data-icon="inline-start" /> : null}
          Save
        </Button>
      </div>
    </div>
  );
}

/** Forget: the owner deletes the day, behind a confirm. */
function ForgetDay({ note, agent, label }: { note: MemoryNote; agent: Agent; label: string }) {
  const [open, setOpen] = useState(false);
  const forget = useForgetNote(agent.id, note.id);
  return (
    <>
      <Button
        variant="ghost"
        size="xs"
        className="text-muted-foreground"
        data-note-forget
        onClick={() => setOpen(true)}
      >
        Forget
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Forget {label}?</AlertDialogTitle>
            <AlertDialogDescription>
              {agentPhrase(agent).replace(/^the /, "The ")} won't see this day's notes again, and
              they can't be brought back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {forget.error ? (
            <p role="alert" className="text-meta text-brick">
              {errorHint(forget.error)}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={forget.isPending}
              data-note-forget-confirm
              onClick={() => forget.mutate(undefined, { onSuccess: () => setOpen(false) })}
            >
              Forget
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
